import axios from 'axios';
import { isTokenExpired, getTokenExpirationTime } from '../utils/jwtUtils';

// Create axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth endpoints that don't need token
const AUTH_ENDPOINTS = [
  '/Corrohealth/auth/login',
  '/Corrohealth/login',
  '/Corrohealth/mobile-login',
  '/Corrohealth/auth/send-otp',
  '/Corrohealth/send-otp',
  '/Corrohealth/auth/verify-otp',
  '/Corrohealth/verify-otp',
  '/Corrohealth/auth/send-email-otp',
  '/Corrohealth/auth/verify-email-otp',
  '/Corrohealth/auth/refresh'
];

// Track if refresh is in progress to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Function to refresh the access token
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    // Use axios directly to avoid interceptor loop
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/Corrohealth/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    if (response.data?.status === 200 && response.data?.data) {
      const { accessToken, refreshToken: newRefreshToken } = response.data.data;

      // Update stored tokens
      localStorage.setItem('authToken', accessToken);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      return accessToken;
    }

    throw new Error('Invalid refresh response');
  } catch (error) {
    // Clear all tokens on refresh failure
    clearAuthTokens();
    throw error;
  }
};

// Clear all auth tokens
const clearAuthTokens = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  sessionStorage.removeItem('authToken');
  localStorage.removeItem('token');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userRole');
};

// Check if token is about to expire (within 2 minutes)
const isTokenAboutToExpire = (token) => {
  try {
    const expTime = getTokenExpirationTime(token);
    if (!expTime) return false;
    const now = Date.now();
    const twoMinutes = 2 * 60 * 1000;
    return (expTime - now) < twoMinutes;
  } catch {
    return false;
  }
};

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    const isAuthEndpoint = AUTH_ENDPOINTS.some(endpoint => config.url?.includes(endpoint));

    if (!isAuthEndpoint) {
      // Get token from localStorage
      const rawToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || localStorage.getItem('token');
      let token = rawToken ? rawToken.trim().replace(/^"|"$/g, '').replace(/^Bearer\s+/i, '') : null;

      if (token) {
        // Check if token is expired or about to expire
        if (isTokenExpired(token) || isTokenAboutToExpire(token)) {
          // Try to refresh the token
          if (!isRefreshing) {
            isRefreshing = true;

            try {
              token = await refreshAccessToken();
              processQueue(null, token);
            } catch (refreshError) {
              processQueue(refreshError, null);
              // Redirect to login only if not already there
              if (window.location.pathname !== '/signin') {
                window.location.href = '/signin';
              }
              return Promise.reject(new axios.Cancel('Session expired. Please login again.'));
            } finally {
              isRefreshing = false;
            }
          } else {
            // Wait for the refresh to complete
            return new Promise((resolve, reject) => {
              failedQueue.push({
                resolve: (newToken) => {
                  config.headers.Authorization = `Bearer ${newToken}`;
                  resolve(config);
                },
                reject: (err) => {
                  reject(err);
                }
              });
            });
          }
        }

        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      const isAuthEndpoint = AUTH_ENDPOINTS.some(endpoint => originalRequest?.url?.includes(endpoint));

      if (!isAuthEndpoint) {
        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const newToken = await refreshAccessToken();
            processQueue(null, newToken);

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            // Redirect to login
            if (window.location.pathname !== '/signin') {
              window.location.href = '/signin';
            }
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else {
          // Wait for refresh to complete then retry
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (newToken) => {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                resolve(axiosInstance(originalRequest));
              },
              reject: (err) => {
                reject(err);
              }
            });
          });
        }
      }
    }

    if (error.response?.status === 403) {
      // Forbidden
      console.error('Access forbidden');
    }

    if (error.response?.status >= 500) {
      // Server error
      console.error('Server error occurred');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
